import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Users, 
  MapPin, 
  Clock, 
  CheckCircle, 
  Coffee, 
  GraduationCap,
  Heart,
  TrendingUp,
  Mail,
  Phone,
  ArrowRight,
  Building,
  Car,
  Banknote
} from "lucide-react";
import { jobsAPI } from "../services/api";

const Career = () => {
  const [jobOpenings, setJobOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJobOpenings = async () => {
      try {
        setLoading(true);
        const jobs = await jobsAPI.getOpenings();
        setJobOpenings(jobs);
      } catch (error) {
        console.error('Error fetching job openings:', error);
        setError('Fehler beim Laden der Stellenausschreibungen');
      } finally {
        setLoading(false);
      }
    };

    fetchJobOpenings();
  }, []);

  const benefits = [
    {
      icon: Clock,
      title: "Flexible Arbeitszeiten",
      description: "Work-Life-Balance ist uns wichtig"
    },
    {
      icon: Building,
      title: "Home Office",
      description: "Remote Work nach Absprache möglich"
    },
    {
      icon: GraduationCap,
      title: "Weiterbildung",
      description: "Regelmäßige Schulungen und Zertifizierungen"
    },
    {
      icon: Banknote,
      title: "Attraktive Vergütung",
      description: "Leistungsgerechte Bezahlung + Boni"
    },
    {
      icon: Car,
      title: "Firmenwagen",
      description: "Für Außendienst-Mitarbeiter verfügbar"
    },
    {
      icon: Coffee,
      title: "Teamevents",
      description: "Regelmäßige Team-Building Aktivitäten"
    },
    {
      icon: Heart,
      title: "Gesundheit",
      description: "Betriebliche Krankenversicherung"
    },
    {
      icon: TrendingUp,
      title: "Karrierechancen",
      description: "Individuelle Entwicklungspfade"
    }
  ];

  const companyValues = [
    {
      title: "Innovation",
      description: "Wir setzen auf modernste Technologien und innovative Lösungen"
    },
    {
      title: "Teamwork",
      description: "Gemeinsam erreichen wir mehr - Zusammenarbeit ist unser Erfolgsrezept"
    },
    {
      title: "Qualität",
      description: "Höchste Standards in allen Bereichen unserer Arbeit"
    },
    {
      title: "Vertrauen",
      description: "Offene Kommunikation und gegenseitiges Vertrauen"
    }
  ];

  const workEnvironment = [
    "Moderne Büroräume mit neuester Technik",
    "Flexible Home-Office Regelungen",
    "Flache Hierarchien und kurze Entscheidungswege",
    "Regelmäßige Team-Meetings und Knowledge-Sharing",
    "Entspannte Arbeitsatmosphäre mit Kollegen, die sich gegenseitig unterstützen",
    "Möglichkeit zur eigenverantwortlichen Projektgestaltung"
  ];

  return (
    <div className="min-h-screen py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <Badge className="bg-purple-100 text-purple-800">Karriere bei NetSyTe</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Gestalten Sie die Zukunft der IT mit uns
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Werden Sie Teil unseres dynamischen Teams und entwickeln Sie innovative 
            IT-Lösungen für Unternehmen in ganz Deutschland.
          </p>
        </div>

        {/* Why Work With Us */}
        <section className="mb-20">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Warum NetSyTe der richtige Arbeitgeber für Sie ist
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-12">
            <div>
              <img
                src="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxvZmZpY2UlMjB0ZWFtfGVufDB8fHx8MTc1ODA5NzYxOXww&ixlib=rb-4.1.0&q=85"
                alt="Team at NetSyTe"
                className="w-full rounded-2xl shadow-xl"
              />
            </div>
            
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">Ein Arbeitsplatz, der Spaß macht</h3>
              <p className="text-lg text-gray-600">
                Bei NetSyTe arbeiten Sie in einem innovativen Umfeld mit 
                modernster Technologie und einem Team von Experten, die 
                Leidenschaft für IT-Lösungen teilen.
              </p>
              
              <ul className="space-y-3">
                {workEnvironment.map((item, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-20 bg-gray-50 p-8 rounded-2xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Ihre Vorteile bei NetSyTe
            </h2>
            <p className="text-lg text-gray-600">
              Wir bieten mehr als nur einen Job - wir bieten eine Karriere
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center p-4">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                  <benefit.icon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Open Positions */}
        <section className="mb-20">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Aktuelle Stellenausschreibungen
            </h2>
            <p className="text-lg text-gray-600">
              Finden Sie Ihre neue berufliche Herausforderung
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-lg">Stellenausschreibungen werden geladen...</div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">{error}</div>
              <Button onClick={() => window.location.reload()}>
                Erneut versuchen
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {jobOpenings.map((job) => (
              <Card key={job.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <CardTitle className="text-xl">{job.title}</CardTitle>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center text-gray-600">
                          <MapPin className="h-4 w-4 mr-1" />
                          <span className="text-sm">{job.location}</span>
                        </div>
                        <Badge>{job.type}</Badge>
                      </div>
                    </div>
                    <Button asChild>
                      <Link to="/contact">
                        Jetzt bewerben
                      </Link>
                    </Button>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <p className="text-gray-600 mb-6">{job.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3 text-red-600">Anforderungen:</h4>
                      <ul className="space-y-2">
                        {job.requirements.map((req, index) => (
                          <li key={index} className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-3 text-green-600">Was wir bieten:</h4>
                      <ul className="space-y-2">
                        {job.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
              ))}
            </div>
          )}
        </section>

        {/* Company Values */}
        <section className="mb-20">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Unsere Unternehmenswerte
            </h2>
            <p className="text-lg text-gray-600">
              Was uns als Team ausmacht und antreibt
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((value, index) => (
              <Card key={index} className="text-center p-6">
                <CardHeader>
                  <CardTitle className="text-lg">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Application Process */}
        <section className="mb-20 bg-blue-50 p-8 rounded-2xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              So läuft Ihre Bewerbung ab
            </h2>
            <p className="text-lg text-gray-600">
              Einfach und transparent zu Ihrem neuen Job
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full font-bold text-xl">
                1
              </div>
              <h3 className="text-lg font-semibold">Bewerbung</h3>
              <p className="text-sm text-gray-600">
                Senden Sie uns Ihre Unterlagen per E-Mail oder über unser Kontaktformular.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full font-bold text-xl">
                2
              </div>
              <h3 className="text-lg font-semibold">Erstkontakt</h3>
              <p className="text-sm text-gray-600">
                Wir melden uns binnen 48h für ein erstes Telefongespräch.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full font-bold text-xl">
                3
              </div>
              <h3 className="text-lg font-semibold">Vorstellungsgespräch</h3>
              <p className="text-sm text-gray-600">
                Persönliches Gespräch vor Ort oder per Video-Call.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full font-bold text-xl">
                4
              </div>
              <h3 className="text-lg font-semibold">Start</h3>
              <p className="text-sm text-gray-600">
                Willkommen im Team! Ihre Einarbeitung beginnt.
              </p>
            </div>
          </div>
        </section>

        {/* Initiative Applications */}
        <section className="mb-20">
          <Card className="p-8 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="text-center space-y-6">
              <Users className="h-16 w-16 text-purple-600 mx-auto" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Initiativbewerbung
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Sie finden keine passende Stelle? Bewerben Sie sich initiativ! 
                  Wir sind immer auf der Suche nach talentierten IT-Experten.
                </p>
              </div>
              <Button size="lg" asChild>
                <Link to="/contact">
                  Initiativ bewerben
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </Card>
        </section>

        {/* Contact for Applications */}
        <section className="text-center space-y-8 bg-gray-900 text-white p-12 rounded-2xl">
          <h2 className="text-3xl font-bold">
            Haben Sie Fragen zu unseren Stellenangeboten?
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Unser HR-Team steht Ihnen gerne für Fragen zur Verfügung.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="flex items-center justify-center">
              <Mail className="mr-2 h-5 w-5" />
              <span>karriere@netsyte.de</span>
            </div>
            <div className="flex items-center justify-center">
              <Phone className="mr-2 h-5 w-5" />
              <span>+49 (0) 123 456 7890</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Career;